// Account Rating Tab
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent } from '../ui/dialog';
import { Loader2, Save, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const AccountRatingTab = ({ tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    rating_criteria_type: 'revenue_ytd',
    rating_criteria_currency: 'USD',
    rating_frequency: 'monthly',
    rating_a_high: 1000000,
    rating_a_low: 500000,
    rating_b_high: 499999,
    rating_b_low: 250000,
    rating_c_high: 249999,
    rating_c_low: 100000,
    rating_d_high: 99999,
    rating_d_low: 0,
    rating_start_date: '',
    rating_paused: false
  });

  useEffect(() => {
    fetchSettings();
  }, [tenantId]);

  const fetchSettings = async () => {
    try {
      const response = await api.get(`/tenants/${tenantId}`);
      const settings = response.data.settings || {};
      setForm({
        rating_criteria_type: settings.rating_criteria_type || 'revenue_ytd',
        rating_criteria_currency: settings.rating_criteria_currency || 'USD',
        rating_frequency: settings.rating_frequency || 'monthly',
        rating_a_high: settings.rating_a_high || 1000000,
        rating_a_low: settings.rating_a_low || 500000,
        rating_b_high: settings.rating_b_high || 499999,
        rating_b_low: settings.rating_b_low || 250000,
        rating_c_high: settings.rating_c_high || 249999,
        rating_c_low: settings.rating_c_low || 100000,
        rating_d_high: settings.rating_d_high || 99999,
        rating_d_low: settings.rating_d_low || 0,
        rating_start_date: settings.rating_start_date || '',
        rating_paused: settings.rating_paused || false
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load rating settings');
    } finally {
      setLoading(false);
    }
  };

  const [runningRatings, setRunningRatings] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.get(`/tenants/${tenantId}`);
      const currentSettings = response.data.settings || {};
      
      await api.put(`/tenants/${tenantId}`, {
        settings: {
          ...currentSettings,
          ...form
        }
      });
      fetchSettings();
      setSaving(false);

      // Check if start date is today or in the past and not paused
      if (form.rating_start_date && !form.rating_paused) {
        const startDate = new Date(form.rating_start_date + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (startDate <= today) {
          setRunningRatings(true);
          try {
            const result = await api.post('/crm-entities/run-automatic-ratings');
            setRunningRatings(false);
            await new Promise(r => setTimeout(r, 300));
            toast.success(`Ratings updated for ${result.data.updated} of ${result.data.total} accounts`);
          } catch {
            setRunningRatings(false);
            await new Promise(r => setTimeout(r, 300));
            toast.error('Failed to run automatic ratings');
          }
          return;
        }
      }
      toast.success('Rating criteria saved');
    } catch (error) {
      console.error('Error saving:', error);
      setSaving(false);
      toast.error('Failed to save rating criteria');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Rating Criteria Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Criteria Type</Label>
              <Select
                value={form.rating_criteria_type}
                onValueChange={(value) => setForm({...form, rating_criteria_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue_ytd">Commissions (Year to Date)</SelectItem>
                  <SelectItem value="revenue_all">Commissions (All Time)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={form.rating_criteria_currency}
                onValueChange={(value) => setForm({...form, rating_criteria_currency: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Rating Frequency</Label>
              <Select
                value={form.rating_frequency}
                onValueChange={(value) => setForm({...form, rating_frequency: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="semi-annually">Semi-Annually</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                How often accounts are automatically re-rated
              </p>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={form.rating_start_date || ''}
                onChange={(e) => setForm({...form, rating_start_date: e.target.value})}
                className={form.rating_start_date ? 'text-foreground' : ''}
                style={form.rating_start_date ? { color: '#000' } : {}}
              />
              <p className="text-xs text-muted-foreground">
                When automatic rating begins
              </p>
            </div>

            {/* Pause Toggle */}
            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant={form.rating_paused ? 'outline' : 'default'}
                  size="sm"
                  className={!form.rating_paused ? 'bg-primary hover:bg-primary/90 text-white' : ''}
                  onClick={() => setForm({...form, rating_paused: false})}
                >
                  Active
                </Button>
                <Button
                  variant={form.rating_paused ? 'default' : 'outline'}
                  size="sm"
                  className={form.rating_paused ? 'bg-yellow-600 hover:bg-yellow-700 text-white' : ''}
                  onClick={() => setForm({...form, rating_paused: true})}
                >
                  Paused
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {form.rating_paused ? 'Automatic rating is paused. Accounts will keep their current ratings.' : 'Automatic rating is active.'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Set commission thresholds for each rating tier. Accounts will be automatically rated based on these criteria.
            </p>
            
            {/* Rating A */}
            <div className="p-4 border rounded bg-green-50">
              <p className="font-semibold text-green-900 mb-3">A - Top Priority</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Minimum</Label>
                  <Input
                    type="number"
                    value={form.rating_a_low}
                    onChange={(e) => setForm({...form, rating_a_low: parseFloat(e.target.value) || 0})}
                    placeholder="500000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maximum</Label>
                  <Input
                    type="number"
                    value={form.rating_a_high}
                    onChange={(e) => setForm({...form, rating_a_high: parseFloat(e.target.value) || 0})}
                    placeholder="1000000+"
                  />
                </div>
              </div>
            </div>

            {/* Rating B */}
            <div className="p-4 border rounded bg-blue-50">
              <p className="font-semibold text-blue-900 mb-3">B - High Priority</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Minimum</Label>
                  <Input
                    type="number"
                    value={form.rating_b_low}
                    onChange={(e) => setForm({...form, rating_b_low: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maximum</Label>
                  <Input
                    type="number"
                    value={form.rating_b_high}
                    onChange={(e) => setForm({...form, rating_b_high: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            </div>

            {/* Rating C */}
            <div className="p-4 border rounded bg-yellow-50">
              <p className="font-semibold text-yellow-900 mb-3">C - Normal</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Minimum</Label>
                  <Input
                    type="number"
                    value={form.rating_c_low}
                    onChange={(e) => setForm({...form, rating_c_low: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maximum</Label>
                  <Input
                    type="number"
                    value={form.rating_c_high}
                    onChange={(e) => setForm({...form, rating_c_high: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            </div>

            {/* Rating D */}
            <div className="p-4 border rounded bg-orange-50">
              <p className="font-semibold text-orange-900 mb-3">D - Low Priority</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Minimum</Label>
                  <Input
                    type="number"
                    value={form.rating_d_low}
                    onChange={(e) => setForm({...form, rating_d_low: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maximum</Label>
                  <Input
                    type="number"
                    value={form.rating_d_high}
                    onChange={(e) => setForm({...form, rating_d_high: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Running Ratings Dialog */}
      <Dialog open={runningRatings} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-sm">
          <div className="flex flex-col items-center gap-4 py-6">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <div className="text-center">
              <p className="text-sm font-semibold">Updating Automatic Ratings</p>
              <p className="text-xs text-muted-foreground mt-1">Comparing account commissions against rating thresholds. This should be completed momentarily.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountRatingTab;
